import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../services/api';

const formatNumber = (value) => {
  if (value === null || value === undefined) return 'Not set';
  return Number(value).toLocaleString();
};

const formatMonthLabel = (periodId) => {
  if (!periodId) return '';
  const value = String(periodId).padStart(6, '0');
  const year = Number(value.slice(0, 4));
  const monthIndex = Number(value.slice(4, 6)) - 1;
  if (Number.isNaN(year) || Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return value;
  }
  const date = new Date(year, monthIndex, 1);
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

const ApiKeyUsage = () => {
  const [usageRows, setUsageRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [keyFilter, setKeyFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [sortBy, setSortBy] = useState('period_id');
  const [sortDir, setSortDir] = useState('desc');
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keyParam = params.get('key');
    setKeyFilter(keyParam || '');
  }, [location.search]);

  useEffect(() => {
    const fetchUsage = async () => {
      setLoading(true);
      try {
        const response = await api.get('/acct-mgmt/v1/account/api-keys/usage');
        setUsageRows(response.data || []);
      } catch (err) {
        setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load API key usage.' });
      } finally {
        setLoading(false);
      }
    };
    fetchUsage();
  }, []);

  useEffect(() => {
    if (!keyFilter) return;
    const exists = usageRows.some((row) => String(row.key_id_public) === String(keyFilter));
    if (!exists && usageRows.length > 0) {
      setKeyFilter('');
    }
  }, [usageRows, keyFilter]);

  const keyOptions = useMemo(() => {
    const map = new Map();
    usageRows.forEach((row) => {
      if (!row.key_id_public) return;
      if (!map.has(row.key_id_public)) {
        map.set(row.key_id_public, row.friendly_name || '');
      }
    });
    return Array.from(map.entries()).map(([keyId, name]) => ({ keyId, name }));
  }, [usageRows]);

  const monthOptions = useMemo(() => {
    const unique = Array.from(new Set(usageRows.map((row) => row.period_id))).filter(Boolean);
    return unique.sort((a, b) => b - a);
  }, [usageRows]);

  const filteredRows = useMemo(() => {
    const normalizedKeyFilter = (keyFilter || '').trim();
    return usageRows.filter((row) => {
      const matchesKey = normalizedKeyFilter ? String(row.key_id_public) === String(normalizedKeyFilter) : true;
      const matchesMonth = monthFilter ? String(row.period_id) === String(monthFilter) : true;
      return matchesKey && matchesMonth;
    });
  }, [usageRows, keyFilter, monthFilter]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((a, b) => {
      let left = a[sortBy];
      let right = b[sortBy];
      if (sortBy === 'friendly_name' || sortBy === 'key_id_public') {
        left = (left || '').toString().toLowerCase();
        right = (right || '').toString().toLowerCase();
        if (left < right) return sortDir === 'asc' ? -1 : 1;
        if (left > right) return sortDir === 'asc' ? 1 : -1;
        return 0;
      }
      const leftNum = Number(left || 0);
      const rightNum = Number(right || 0);
      return sortDir === 'asc' ? leftNum - rightNum : rightNum - leftNum;
    });
    return rows;
  }, [filteredRows, sortBy, sortDir]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const exportCsv = () => {
    const rows = usageRows.map((row) => ({
      name: row.friendly_name || '',
      publicKey: row.key_id_public || '',
      month: formatMonthLabel(row.period_id),
      requests: row.request_count ?? 0,
    }));
    const headers = ['API Key Name', 'Public Key', 'Month', 'Number of Requests'];
    const csvLines = [
      headers.join(','),
      ...rows.map((row) => [
        `"${row.name.replace(/"/g, '""')}"`,
        `"${row.publicKey.replace(/"/g, '""')}"`,
        `"${row.month.replace(/"/g, '""')}"`,
        row.requests,
      ].join(',')),
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'api-key-usage.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <div className="mb-2">
        <Link className="btn btn-link p-0" to="/account/api-keys">
          &lt; Back to API Keys
        </Link>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="card-title d-flex flex-wrap align-items-center justify-content-between w-100 mb-3">
            <div>
              <h5 className="mb-0">API Key Usage</h5>
              <div className="text-muted small">
                Usage figures are near real-time and may be delayed by a few minutes.
              </div>
            </div>
            <button className="btn btn-outline-primary btn-sm" type="button" onClick={exportCsv}>
              <i className="fas fa-file-csv me-2"></i>
              Export CSV
            </button>
          </div>
        {status.message && (
          <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
        )}
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <label className="form-label">Filter by API Key</label>
            <select
              className="form-select"
              value={keyFilter}
              onChange={(e) => setKeyFilter(e.target.value)}
            >
              <option value="">All keys</option>
              {keyOptions.map((option) => (
                <option key={option.keyId} value={option.keyId}>
                  {option.name ? `${option.name} (${option.keyId})` : option.keyId}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Filter by Month</label>
            <select
              className="form-select"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
            >
              <option value="">All months</option>
              {monthOptions.map((periodId) => (
                <option key={periodId} value={periodId}>
                  {formatMonthLabel(periodId)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="text-muted">Loading usage...</div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th role="button" onClick={() => handleSort('friendly_name')}>
                    API Key Name
                  </th>
                  <th role="button" onClick={() => handleSort('key_id_public')}>
                    Public Key
                  </th>
                  <th role="button" onClick={() => handleSort('period_id')}>
                    Month
                  </th>
                  <th role="button" onClick={() => handleSort('request_count')}>
                    Number of Requests
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, idx) => (
                  <tr key={`${row.key_id_public}-${row.period_id}-${idx}`}>
                    <td>{row.friendly_name || '—'}</td>
                    <td className="text-muted">{row.key_id_public}</td>
                    <td>{formatMonthLabel(row.period_id)}</td>
                    <td>{formatNumber(row.request_count ?? 0)}</td>
                  </tr>
                ))}
                {sortedRows.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-muted text-center">No usage data found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeyUsage;
